import { ReactNode } from 'react';
import { Badge, Box, Group, Tooltip } from '@mantine/core';
import { IconLink, IconLinkPlus } from '@tabler/icons-react';
import { useFlags } from 'src/feature-flags/FlagsHook';

type FeedFlags = {
  isUpdatedAutomatically?: boolean;
  hasFeedData?: boolean;
};

const CONNECTED_ROW_TOOLTIP = 'This asset updates automatically from a connected data feed.';
const CONNECTABLE_ROW_TOOLTIP =
  'A data feed matches this asset. Open it to connect and keep it updated automatically.';
const CONNECTED_ACCORDION_TOOLTIP =
  'Some assets here update automatically from a connected data feed.';
const CONNECTABLE_ACCORDION_TOOLTIP = 'Some assets here have a matching data feed you can connect.';

function FeedIcon({
  background,
  icon,
  tooltip,
  ml = 0,
}: {
  background: string;
  icon: ReactNode;
  tooltip: string;
  ml?: number;
}) {
  return (
    <Tooltip label={tooltip} position="bottom" withArrow multiline w={220}>
      <Box
        component="span"
        ml={ml}
        w={20}
        h={20}
        style={{
          backgroundColor: background,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
          flexShrink: 0,
          cursor: 'help',
        }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
}

export function AssetFeedRowIcon({ isUpdatedAutomatically, hasFeedData }: FeedFlags) {
  const { flagEnabledAssetFeedIndicators } = useFlags();

  if (!flagEnabledAssetFeedIndicators) {
    return null;
  }

  if (isUpdatedAutomatically) {
    return (
      <FeedIcon
        ml={4}
        background="var(--mantine-color-blue-1)"
        tooltip={CONNECTED_ROW_TOOLTIP}
        icon={<IconLink size={12} style={{ color: 'var(--mantine-color-blue-6)' }} />}
      />
    );
  }

  if (hasFeedData) {
    return (
      <FeedIcon
        ml={4}
        background="var(--mantine-color-teal-1)"
        tooltip={CONNECTABLE_ROW_TOOLTIP}
        icon={<IconLinkPlus size={12} style={{ color: 'var(--mantine-color-teal-7)' }} />}
      />
    );
  }

  return null;
}

export function AssetFeedIcons({ items }: { items: FeedFlags[] }) {
  const { flagEnabledAssetFeedIndicators } = useFlags();
  const hasConnected = items.some(item => item.isUpdatedAutomatically);
  const hasConnectable = items.some(item => item.hasFeedData && !item.isUpdatedAutomatically);

  if (!flagEnabledAssetFeedIndicators || (!hasConnected && !hasConnectable)) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {hasConnected && (
        <FeedIcon
          background="var(--mantine-color-blue-1)"
          tooltip={CONNECTED_ACCORDION_TOOLTIP}
          icon={<IconLink size={12} style={{ color: 'var(--mantine-color-blue-6)' }} />}
        />
      )}
      {hasConnectable && (
        <FeedIcon
          background="var(--mantine-color-teal-1)"
          tooltip={CONNECTABLE_ACCORDION_TOOLTIP}
          icon={<IconLinkPlus size={12} style={{ color: 'var(--mantine-color-teal-7)' }} />}
        />
      )}
    </Group>
  );
}

export function AssetFeedBadges({ items }: { items: FeedFlags[] }) {
  const { flagEnabledAssetFeedIndicators } = useFlags();
  const hasConnected = items.some(item => item.isUpdatedAutomatically);
  const hasConnectable = items.some(item => item.hasFeedData && !item.isUpdatedAutomatically);

  if (!flagEnabledAssetFeedIndicators || (!hasConnected && !hasConnectable)) {
    return null;
  }

  return (
    <Group gap="xs" wrap="nowrap">
      {hasConnected && (
        <Tooltip label={CONNECTED_ACCORDION_TOOLTIP} position="bottom" withArrow multiline w={230}>
          <Badge
            variant="light"
            color="blue"
            size="lg"
            leftSection={<IconLink size={14} />}
            style={{ cursor: 'help' }}
          >
            Connected data
          </Badge>
        </Tooltip>
      )}
      {hasConnectable && (
        <Tooltip
          label={CONNECTABLE_ACCORDION_TOOLTIP}
          position="bottom"
          withArrow
          multiline
          w={230}
        >
          <Badge
            variant="light"
            color="teal"
            size="lg"
            leftSection={<IconLinkPlus size={14} />}
            style={{ cursor: 'help' }}
          >
            Data to connect
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}
